/** @jsx jsx */
import { jsx, css } from '@emotion/core';

import React, {
  Fragment,
  useCallback,
  useState,
  useContext,
  useEffect,
} from 'react';
import { createStyles, makeStyles, Theme } from '@material-ui/core/styles';
import {
  Grid,
  Box,
  Link,
  CircularProgress,
  FormHelperText,
} from '@material-ui/core';
import { ErrorCode, useDropzone } from 'react-dropzone';
import UploadIcon from './static/uploadIcon.svg';
import colors from '../../themes/main-colors';
import useFileUploadApi from '../../hooks/pge-plus/useFileUploadApi';
import { NotificationsContext } from '../../providers/NotificationsProvider';
import useContentMgmt from '../../hooks/useContentMgmt';
import microcopyGroupIds from './microcopyGroupIds';

const useStyles = makeStyles((theme: Theme) =>
  createStyles({
    link: {
      fontSize: '16px !important',
      padding: '0px !important',
      color: `${colors.sparkBlue} !important`,
      fontWeight: 600,
    },
    image: {
      border: `2px solid ${colors.sparkBlue} !important`,
      borderRadius: '5px',
    },
  }),
);

type Props = {
  placeholder: React.ReactNode;
  reuploadLabel: string;
  image?: string | undefined; //S3 file object name for already uploaded image. eg., a72a7e65-25e3-4f7a-8f02-2b10febf92d3.jpg
  onUpload: (pgeStorekey: string) => void;
  style?: React.CSSProperties | undefined;
  acceptMimeTypes?: Record<string, []>;
  maxFileSize?: number;
};

export const DEFAULT_MIME_TYPES: Record<string, []> = {
  'image/png': [],
  'image/jpeg': [],
};
const DEFAULT_MAX_FILE_SIZE = 1024 * 1024 * 30; //30MB

export const FILE_TYPE_PDF = 'application/pdf';

const MimeTypeMap: { [key: string]: string } = {
  'image/png': 'PNG',
  'image/jpeg': 'JPG',
  [FILE_TYPE_PDF]: 'PDF',
};

export const MIME_TYPE_PDF: Record<string, []> = {
  [FILE_TYPE_PDF]: [],
};

/**
 * Checks if the provided URL points to a PDF file.
 * @param url - The URL to check.
 * @returns True if the URL is a PDF file, false otherwise.
 */
export function isPdfFile(url: string): boolean {
  // Use a URL object to parse the URL and get the pathname
  try {
    const urlObj = new URL(url);
    const pathname = urlObj.pathname;

    // Get the file extension by splitting the pathname by '.' and taking the last part
    const extension = pathname
      .split('.')
      .pop()
      ?.toLowerCase();

    // Check if the extension is 'pdf'
    return extension === 'pdf';
  } catch (error) {
    return false;
  }
}

export const getSupportedFileNames = (mimeTypes: string[]) => {
  const fileTypes = mimeTypes?.map(mimeType => MimeTypeMap[mimeType] || null);
  return fileTypes.filter(v => v).join(', ');
};

export const FileUploader = ({
  image,
  placeholder,
  reuploadLabel,
  onUpload,
  style,
  acceptMimeTypes = DEFAULT_MIME_TYPES,
  maxFileSize = DEFAULT_MAX_FILE_SIZE,
}: Props) => {
  const [filePreview, setFilePreview] = useState<string>();
  const [fileType, setFileType] = useState<string>();

  const { uploadFile, loading, getPgeStoreDownloadUrl } = useFileUploadApi();
  const notificationContext = useContext(NotificationsContext);
  const [fileUploadError, setFileUploadError] = useState<React.ReactNode>('');

  const { content, inlineRichText } = useContentMgmt(
    microcopyGroupIds.PGE_PLUS_COMMON_FILE_UPLOAD_COMPONENT,
  );

  const classes = useStyles();
  useEffect(() => {
    //Get the download URL for the image
    if (image) {
      void getImageUrl(image);
    }
  }, [image]);

  const getImageUrl = async (key: string) => {
    //Get the file download URL
    try {
      const images = await getPgeStoreDownloadUrl({ keys: [key] });
      if (isPdfFile(images[0].downloadUrl)) {
        setFileType(FILE_TYPE_PDF);
      }
      setFilePreview(images[0].downloadUrl);
    } catch (e) {
      notificationContext.setState({
        isOpen: true,
        severity: 'error',
        variant: 'filled',
        message: content.get('FILE_UPLOAD_FAILED_MESSAGE'),
      });
      window.scrollTo(0, 0);
    }
  };

  const onDrop = useCallback(
    async (acceptedFiles: File[]) => {
      setFileType(acceptedFiles[0].type);
      setFilePreview(URL.createObjectURL(acceptedFiles[0]));
      setFileUploadError('');
      const response = await uploadFile(acceptedFiles[0]);

      if (response.success && response.pgeStoreKey) {
        onUpload(response.pgeStoreKey);
      } else {
        notificationContext.setState({
          isOpen: true,
          severity: 'error',
          variant: 'filled',
          message: content.get('FILE_UPLOAD_FAILED_MESSAGE'),
        });
        window.scrollTo(0, 0);
        setFilePreview(undefined);
      }
    },
    [content],
  );

  const { getRootProps, getInputProps, isDragActive, open } = useDropzone({
    onDropAccepted: onDrop,
    onDropRejected: rejectedFiles => {
      const errorCode = rejectedFiles[0]?.errors[0]?.code;
      if (errorCode === ErrorCode.FileInvalidType) {
        setFileUploadError(
          inlineRichText(content.get('INCORRECT_FILE_TYPE') || '', {
            SUPPORTED_FILE_TYPES: getSupportedFileNames(
              Object.keys(acceptMimeTypes),
            ),
          }) || '',
        );
      }
      if (errorCode === ErrorCode.FileTooLarge) {
        setFileUploadError(content.get('FILE_SIZE_EXCEEDS_THE_LIMIT') || '');
      }
    },
    accept: acceptMimeTypes,
    maxFiles: 1,
    multiple: false,
    maxSize: maxFileSize,
  });

  const uploadAreaStyle: React.CSSProperties = {
    padding: '24px 0',
    marginTop: 24,
    marginBottom: 8,
    display: 'flex',
    justifyContent: 'center',
    borderRadius: '5px',
    cursor: 'pointer',
    border: '2px dashed ' + colors.sparkBlue,
    backgroundColor: colors.lightBlue1,
  };

  return (
    <Fragment>
      <input {...getInputProps()} />
      {!filePreview && (
        <Grid
          {...getRootProps()}
          css={{
            ...uploadAreaStyle,
            borderColor: fileUploadError ? colors.errorRed : colors.sparkBlue,
            visibility: filePreview ? 'hidden' : 'visible',
          }}
          style={style}
        >
          {isDragActive ? (
            <Box
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                color: colors.sparkBlue,
              }}
            >
              <UploadIcon
                fontSize="large"
                css={{ verticalAlign: 'middle', marginRight: 18 }}
              />{' '}
              {content.get('DROP_FILES_HERE')}
            </Box>
          ) : (
            <Box
              style={{
                fontSize: 16,
                fontWeight: 'bold',
                lineHeight: '20px',
                color: colors.sparkBlue,
              }}
            >
              {' '}
              <UploadIcon
                fontSize="large"
                css={{ verticalAlign: 'middle', marginRight: 18 }}
              />
              {placeholder}
            </Box>
          )}
        </Grid>
      )}
      {filePreview && (
        <Fragment>
          <Box css={{ position: 'relative' as const }}>
            {fileType !== FILE_TYPE_PDF && (
              <img
                className={classes.image}
                src={filePreview}
                css={{
                  width: '100%',
                  maxHeight: '400px',
                  objectFit: 'cover',
                }}
                // Revoke data uri after image is loaded
                onLoad={() => {
                  URL.revokeObjectURL(filePreview);
                }}
              />
            )}
            {fileType === FILE_TYPE_PDF && (
              <Box
                style={{
                  ...uploadAreaStyle,
                }}
              >
                <iframe
                  src={filePreview}
                  css={{ height: '300px', width: '90%' }}
                />
              </Box>
            )}
            {loading && (
              <Box
                css={{
                  display: 'flex',
                  justifyContent: 'center',
                  alignItems: 'center',
                  width: '100%',
                  height: '100%',
                  position: 'absolute' as const,
                  top: 0,
                  background: '#dcdcdc9e',
                }}
              >
                <Box>
                  <CircularProgress />
                </Box>
              </Box>
            )}
          </Box>
          {!loading && (
            <Link
              underline="none"
              component="button"
              variant="body1"
              className={classes.link}
              onClick={e => {
                e.preventDefault();
                e.stopPropagation();
                open();
              }}
            >
              {reuploadLabel}
            </Link>
          )}
        </Fragment>
      )}
      {fileUploadError && (
        <FormHelperText css={{ fontFamily: 'inherit' }} error>
          {fileUploadError}
        </FormHelperText>
      )}
    </Fragment>
  );
};
